<template>
    <div>
        <div ref="d3_root"></div>
    </div>
</template>

<script>
    import * as d3 from "d3";
    import * as d3Sankey from 'd3-sankey';

    export default {
        name: "ChartSankey",
        props: ['height','width', 'text_size', 'chartData','render_info'],
        mounted() {
            if (this.chartData) {
                this.draw()
            }
        },
        watch: {
            chartData: function (newValue) {
                if (newValue) this.draw()
            },
        },
        methods: {
            commarize(number) {
                let min = 1e3;
                // Alter numbers larger than 1k
                if (number >= min) {
                    var units = ["k", "M", "B", "T", "Q"];

                    var order = Math.floor(Math.log(number) / Math.log(1000));

                    var unitname = units[(order - 1)];
                    var num = Math.floor(number / 1000 ** order);

                    // output number remainder + unitname
                    return num + unitname
                }

                // return formatted original number
                return number.toLocaleString()
            },
            // This function is repeated in different components
            draw() {
                let meta = this.chartData.meta
                let link_colors_flat = meta.link_colors_flat

                let data = this.chartData.data

                const margin = 10;
                const width = this.width;
                const height = this.height;
                const svgBackground = "#fff";
                const nodeWidth = 24;
                const nodePadding = 16;
                const nodeOpacity = 0.8;
                const linkOpacity = 0.5;
                const nodeDarkenFactor = 0.3;
                const nodeStrokeWidth = 4;
                const arrow = "\u2192";
                const nodeAlignment = d3Sankey.sankeyCenter;
                const path = d3Sankey.sankeyLinkHorizontal();
                let initialMousePosition = {};
                let initialNodePosition = {};
                function addGradientStop(gradients, offset, fn) {
                    return gradients.append("stop")
                        .attr("offset", offset)
                        .attr("stop-color", fn);
                }
                function color(index) {
                    return link_colors_flat[index];
                }
                function darkenColor(color, factor) {
                    return d3.color(color).darker(factor)
                }
                function cleanForCssId(str) {
                  return str.replace(/^[^a-z]+|[^\w:.-]+/gi, "")
                }
                function getGradientId(d) {
                    let source_clean = cleanForCssId(d.source.id)
                    let target_clean = cleanForCssId(d.target.id)
                    return `gradient_${source_clean}_${target_clean}`;
                }
                function getMousePosition(e) {
                    e = e || d3.event;
                    return {
                        x: e.x,
                        y: e.y
                    };
                }
                function getNodePosition(node) {
                    return {
                        x: +node.attr("x"),
                        y: +node.attr("y"),
                        width: +node.attr("width"),
                        height: +node.attr("height")
                    };
                }
                function moveNode(node, position) {
                    window.node=node
                    position.width = position.width || +(node.attr("width"));
                    position.height = position.height || +(node.attr("height"));
                    if (position.x < 0) {
                        position.x = 0;
                    }
                    if (position.y < 0) {
                        position.y = 0;
                    }
                    if (position.x + position.width > graphSize[0]) {
                        position.x = graphSize[0] - position.width;
                    }
                    if (position.y + position.height > graphSize[1]) {
                        position.y = graphSize[1] - position.height;
                    }
                    node.attr("x", position.x)
                        .attr("y", position.y);
                    let nodeData = node.data()[0];
                    nodeData.x0 = position.x
                    nodeData.x1 = position.x + position.width;
                    nodeData.y0 = position.y;
                    nodeData.y1 = position.y + position.height;

                    d3.select(node.node().parentNode)
                        .selectAll('text')
                        .attr("x", function(d) { return d.x0 + 35; })
                        .attr("y", function(d) { return d.y0 + 10; })
                        .attr("dy", ".35em")
                        .attr("text-anchor", "start")
                        .attr("transform", null)
                        .text(function(d) { return d.name; })
                        .filter(function(d) { return (d.x0 + sankey.nodeWidth() + 150) > ((width - margin)/2); })
                        .attr("x", (function(d) {return d.x0 - sankey.nodeWidth() }))
                        .attr("text-anchor", "end");

                    sankey.update(graph);
                    svgLinks.selectAll("linearGradient")
                        .attr("x1", d => d.source.x1)
                        .attr("x2", d => d.target.x0);
                    svgLinks.selectAll("path")
                        .attr("d", path);
                }
                function onDragDragging() {
                    let currentMousePosition = getMousePosition(d3.event);
                    let delta = {
                        x: currentMousePosition.x - initialMousePosition.x,
                        y: currentMousePosition.y - initialMousePosition.y
                    };
                    let thisNode = d3.select(this);
                    let newNodePosition = {
                        x: initialNodePosition.x + delta.x,
                        y: initialNodePosition.y + delta.y,
                        width: initialNodePosition.width,
                        height: initialNodePosition.height
                    };
                    moveNode(thisNode, newNodePosition);
                }
                function onDragEnd() {
                    let node = d3.select(this)
                        .attr("stroke-width", 0);
                }
                function onDragStart() {
                    let node = d3.select(this)
                        .raise()
                        .attr("stroke-width", nodeStrokeWidth);
                    setInitialNodePosition(node);
                    initialNodePosition = getNodePosition(node);
                    initialMousePosition = getMousePosition(d3.event);
                }
                function reduceUnique(previous, current) {
                    if (previous.indexOf(current) < 0) {
                        previous.push(current);
                    }
                    return previous;
                }
                function setInitialMousePosition(e) {
                    initialMousePosition.x = e.x;
                    initialMousePosition.y = e.y;
                }
                function setInitialNodePosition(node) {
                    let pos = node ? getNodePosition(node) : { x: 0, y: 0, width: 0, height: 0 };
                    initialNodePosition.x = pos.x;
                    initialNodePosition.y = pos.y;
                    initialNodePosition.width = pos.width;
                    initialNodePosition.height = pos.height;
                }
                function sumValues(previous, current) {
                    previous += current;
                    return previous;
                }

                const svg_element = document.createElementNS("http://www.w3.org/2000/svg",'svg');

                const svgRoot = d3.select(svg_element)
                    .attr("width", width)
                    .attr("height", height)
                    .style("background-color", svgBackground);

                let svg = svgRoot
                    .append("g")
                    .attr("transform", `translate(${margin},${margin})`);
                // Define our sankey instance.
                const graphSize = [width - 2*margin, height - 2*margin];
                const sankey = d3Sankey.sankey()
                    .size(graphSize)
                    .nodeId(d => d.id)
                    .nodeWidth(nodeWidth)
                    .nodePadding(nodePadding)
                    .nodeAlign(nodeAlignment);
                if (this.render_info && !this.render_info.display_extra) {
                  sankey.nodeSort(null);
                  console.log('using original sort')
                } {
                  console.log('letting s3 sort')
              }
                let graph = sankey(data);
                // Loop through the nodes. Set additional properties to make a few things
                // easier to deal with later.
                graph.nodes.forEach(node => {
                    let fillColor = node.color;
                    node.fillColor = fillColor;
                    node.strokeColor = darkenColor(fillColor, nodeDarkenFactor);
                    node.width = node.x1 - node.x0;
                    node.height = node.y1 - node.y0;
                });
                // Build the links.
                let svgLinks = svg.append("g")
                    .classed("links", true)
                    .selectAll("g")
                    .data(graph.links)
                    .enter()
                    .append("g");
                let gradients = svgLinks.append("linearGradient")
                    .attr("gradientUnits", "userSpaceOnUse")
                    .attr("x1", d => d.source.x1)
                    .attr("x2", d => d.target.x0)
                    .attr("id", d => getGradientId(d));
                addGradientStop(gradients, 0.0, d => d.source.color);
                addGradientStop(gradients, 1.0, d => d.target.color);

                svgLinks.append("path")
                    .classed("link", true)
                    .attr('id',d => d.source.id + '_' + d.target.id)
                    .attr("d", path)
                    .attr("fill", "none")
                    .attr("stroke", d => `url(#${getGradientId(d)})`)
                    .attr("stroke-width", d => Math.max(1.0, d.width))
                    .attr("stroke-opacity", linkOpacity);
                // Add hover effect to links.
                svgLinks.append("title")
                    .text(d => `${d.source.id} ${arrow} ${d.target.id}\n${d.value}`);
                svgLinks.append("text")
                    .on("click", (d) => {
                      this.$emit('stackChartClicked', {label:d.source.name,color:d.target.name});
                    })
                    .append("textPath") //append a textPath to the text element
                    .attr("xlink:href", d => '#' + d.source.id + '_' + d.target.id) //place the ID of the path here
                    .style("text-anchor","middle") //place the text halfway on the arc
                    .attr("startOffset", (d,i) => {
                        let position = i % 3;
                        if (position===0) {
                            return "33%";
                        } else if (position===1) {
                            return "50%";
                        } else {
                            return "66%";
                        }
                    })
                    .attr('opacity',0.4)
                    .text((d) => {
                        return this.commarize(d.text)
                    });
                let svgNodes = svg.append("g")
                    .classed("nodes", true)
                    .selectAll("g")
                    .data(graph.nodes)
                    .enter()
                    .append("g")
                    .classed('node_wrapper',true)
                    .append("rect")
                    .classed("node", true)
                    .attr("x", d => d.x0)
                    .attr("y", d => d.y0)
                    .attr("width", d => d.width)
                    .attr("height", d => d.height)
                    .attr("fill", d => d.fillColor)
                    .attr("opacity", nodeOpacity)
                    .attr("stroke", d => d.strokeColor)
                    .attr("stroke-width", 0);
                let nodeDepths = graph.nodes
                    .map(n => n.depth)
                    .reduce(reduceUnique, []);
                nodeDepths.forEach(d => {
                    let nodesAtThisDepth = graph.nodes.filter(n => n.depth === d);
                    let numberOfNodes = nodesAtThisDepth.length;
                    let totalHeight = nodesAtThisDepth
                        .map(n => n.height)
                        .reduce(sumValues, 0);
                    let whitespace = graphSize[1] - totalHeight;
                    let balancedWhitespace = whitespace / (numberOfNodes + 1.0);
                    // console.log("depth", d, "total height", totalHeight, "whitespace", whitespace, "balanced whitespace", balancedWhitespace);
                });
                // Add hover effect to nodes.
                svgNodes.append("title")
                    .text(d => `${d.id}\n${d.value} unit(s)`);

                svgNodes.call(d3.drag()
                    .on("start", onDragStart)
                    .on("drag", onDragDragging)
                    .on("end", onDragEnd));

                while (this.$refs.d3_root.firstChild) {
                    this.$refs.d3_root.removeChild(this.$refs.d3_root.firstChild);
                }

                this.$refs.d3_root.append(svgRoot.node())

                d3.selectAll(".node_wrapper")
                    .on("click", (d) => {
                      if (d.x0===0) {
                        this.$emit('stackChartClicked', {label:d.name});
                      } else {
                        this.$emit('stackChartClicked', {color:d.name});
                      }
                    })
                    .insert("text")
                    .attr("x", function(d) { return d.x0 + 35; })
                    .attr("y", function(d) { return d.y0 + 10; })
                    .attr("dy", ".35em")
                    .attr("text-anchor", "start")
                    .attr("transform", null)
                    .text(function(d) { return d.name; })
                    .filter(function(d) { return (d.x0 + sankey.nodeWidth() + 150) > ((width - margin)/2); })
                    .attr("x", (function(d) {return d.x0 - sankey.nodeWidth() }))
                    .attr("text-anchor", "end");
            }
        },
    }
</script>

<style scoped>

</style>